// Shared pricing resolver — single source of truth for how a class's price is
// interpreted and rendered across the site (public cards, detail dialogs,
// registration form previews, admin editor, Operations Board, Pricing Health
// audit). Do not compute prices ad-hoc in components; import from here.
//
// Phase 1 scope: display + admin previews + audit. Checkout, emails, MAVIS,
// snapshots and revenue reporting come in Phase 2 and will also route through
// this file.

import type { ClassSession } from "@/data/classes";

export type BillingUnit = "per_session" | "per_month" | "per_semester";

export const BILLING_UNITS: BillingUnit[] = [
  "per_session",
  "per_month",
  "per_semester",
];

/** Machine → human ("per session"). */
export function billingUnitLabel(u: BillingUnit): string {
  switch (u) {
    case "per_session":
      return "per session";
    case "per_month":
      return "per month";
    case "per_semester":
      return "per semester";
  }
}

/** Machine → title-case ("Per session"). */
export function billingUnitTitle(u: BillingUnit): string {
  const l = billingUnitLabel(u);
  return l.charAt(0).toUpperCase() + l.slice(1);
}

/** Noun form for CTAs: "session" / "month" / "semester". */
export function billingUnitNoun(u: BillingUnit): string {
  return u.replace(/^per_/, "");
}

/** Normalize any legacy or DB value into the canonical enum. */
export function coerceBillingUnit(v: unknown): BillingUnit {
  const s = String(v ?? "").toLowerCase();
  if (s === "per_semester" || s === "semester") return "per_semester";
  if (s === "per_month" || s === "month") return "per_month";
  return "per_session";
}

/** Format a currency amount as "$40" or "$40.50" (drops trailing .00). */
export function formatMoney(amount: number): string {
  if (!Number.isFinite(amount)) return "";
  const rounded = Math.round(amount * 100) / 100;
  return Number.isInteger(rounded)
    ? `$${rounded}`
    : `$${rounded.toFixed(2)}`;
}

export interface PricingInput {
  pricePerChild: number | null | undefined;
  billingUnit: BillingUnit;
  priceDisplay?: string | null;
  /** External-provider or otherwise privately handled → no Silver Sage price line. */
  externalPayment?: boolean;
  /**
   * True when a real instructor is assigned. When false and the class isn't
   * externally managed, we hide the structured price ("Pricing coming soon")
   * unless the admin has set an explicit Price display override.
   */
  hasInstructor?: boolean;
  /**
   * For per_session pricing, how many weeks a single "session" covers.
   * "Per class" style pricing is session_length_weeks = 1.
   * A 4-week bundle at $40 is session_length_weeks = 4.
   */
  sessionLengthWeeks?: number | null;
  /**
   * True when the admin has intentionally chosen to hide the billing unit on
   * the public price display (e.g. show "$40" instead of "$40 per month").
   * The Pricing Health audit uses this to distinguish an intentional bare
   * price from a real conflict — a bare "$40" that matches the structured
   * dollar amount is only "resolved" when this flag is on.
   */
  displayOmitsUnitApproved?: boolean;
}


/** Shown when a class is priced but has no instructor assigned yet. */
export const PRICING_COMING_SOON = "Pricing coming soon";

/** Auto-format from the structured price+unit. Never uses the free-text override. */
export function autoFormattedPrice(input: PricingInput): string {
  const amount = Number(input.pricePerChild);
  if (input.externalPayment) return "See provider";
  if (!Number.isFinite(amount) || amount <= 0) return "Free";
  return `${formatMoney(amount)} ${billingUnitLabel(input.billingUnit)}`;
}

/**
 * Public price label. Uses the intentional Price display override when set,
 * otherwise falls back to the auto-formatted structured price. Returns
 * "Pricing coming soon" when no instructor is assigned and no override exists.
 * This is what every parent-facing surface should render.
 */
export function publicPriceLabel(input: PricingInput): string {
  const override = (input.priceDisplay ?? "").trim();
  if (override) return override;
  if (input.externalPayment) return "See provider";
  if (input.hasInstructor === false) return PRICING_COMING_SOON;
  return autoFormattedPrice(input);
}

/**
 * Does the custom Price display wording match what we'd auto-format?
 * Used to warn admins that their override diverges from the structured price.
 *
 * A bare money label (e.g. "$40") is treated as matching the structured price
 * ONLY when `displayOmitsUnitApproved` is true — the admin has explicitly
 * decided to hide the billing unit from the public display. Without that
 * approval, a bare "$40" against $40/month is a real Pricing Health conflict.
 */
export function displayMatchesAuto(input: PricingInput): boolean {
  const override = (input.priceDisplay ?? "").trim();
  if (!override) return true;
  const auto = autoFormattedPrice(input);
  if (override.toLowerCase() === auto.toLowerCase()) return true;
  if (input.displayOmitsUnitApproved) {
    const amount = Number(input.pricePerChild);
    if (Number.isFinite(amount) && amount > 0) {
      const money = formatMoney(amount);
      if (money && override.toLowerCase() === money.toLowerCase()) return true;
    }
  }
  return false;
}

/* -------------------------------------------------------------------------- */
/* Totals — used by the registration form's live total and admin projections. */
/* Phase 1: these are display helpers only; Phase 2 wires them to checkout.   */
/* -------------------------------------------------------------------------- */

export interface TotalInput {
  billingUnit: BillingUnit;
  pricePerChild: number;
  /** For per_session: which session labels the parent selected. */
  selectedSessions?: string[];
  /** Full session catalog (needed to respect per-session overrides). */
  sessions?: ClassSession[];
  /** Number of children being registered. Defaults to 1. */
  children?: number;
}

/**
 * How much a single child owes for the selected registration.
 * - per_session : sum of selected session prices (respecting overrides)
 * - per_month   : monthly price × 1 (Phase 1: monthly UI charges one month)
 * - per_semester: flat semester price × 1
 */
export function perChildTotal(input: TotalInput): number {
  const { billingUnit, pricePerChild, selectedSessions, sessions } = input;
  const base = Number(pricePerChild) || 0;
  if (billingUnit === "per_semester") return base;
  if (billingUnit === "per_month") return base;
  // per_session
  if (selectedSessions && selectedSessions.length && sessions?.length) {
    return selectedSessions.reduce((sum, label) => {
      const s = sessions.find((x) => x.label === label);
      const p = typeof s?.price === "number" ? s.price : base;
      return sum + p;
    }, 0);
  }
  return base;
}

/** Total charge = per-child total × number of children (pre-discount). */
export function totalCharge(input: TotalInput): number {
  return perChildTotal(input) * Math.max(1, input.children ?? 1);
}

/**
 * Admin projected revenue for a class: price-per-child × enrolled count.
 * For per_session classes without a specific selection this is the base price;
 * revenue reports in Phase 2 will use the payment ledger, not this projection.
 */
export function projectedRevenue(args: {
  billingUnit: BillingUnit;
  pricePerChild: number | null | undefined;
  enrolled: number;
}): number {
  const base = Number(args.pricePerChild) || 0;
  return base * Math.max(0, args.enrolled);
}

/* -------------------------------------------------------------------------- */
/* Semester total — one authoritative computation shared everywhere.          */
/* -------------------------------------------------------------------------- */

export interface SemesterPriceInput {
  billingUnit: BillingUnit;
  pricePerChild: number | null | undefined;
  /** For per_session: how many weeks a single session covers (default 1). */
  sessionLengthWeeks?: number | null;
  /** Instructional weeks in the semester for this class's weekday. Required for
   *  per_session / per_class math. Never hardcoded — pass in from the calendar. */
  instructionalWeeks?: number | null;
  /** Number of months in the semester (for per_month). Required for per_month math. */
  semesterMonths?: number | null;
  externalPayment?: boolean;
  hasInstructor?: boolean;
  priceDisplay?: string | null;
  /**
   * Explicit price stored on the semester session row itself (classes.sessions
   * `price`). This is the authoritative amount the parent is charged for that
   * period, so it always wins over the derived billing-unit math. Example:
   * Chess Club bills $40/month for four-week sessions, but the Fall Semester
   * row carries a $120 price (12 meetings × $10).
   */
  semesterPriceOverride?: number | null;
}

/**
 * Compute the total semester cost per child based on the class's billing unit.
 * Returns null when we can't compute (missing price, missing weeks/months, or
 * the class has no instructor / is external).
 *
 * Rules (from the pricing spec):
 *   per_class / per_session with 1-week length  → price × instructional_weeks
 *   per_session with N-week bundles             → (price ÷ N) × instructional_weeks
 *   per_month                                   → price × semester_months
 *   per_semester                                → price (stored directly)
 */
export function computeSemesterTotal(input: SemesterPriceInput): number | null {
  if (input.externalPayment) return null;
  // A price stored directly on the semester session row is the amount checkout
  // charges, so every surface must show exactly that — even when the legacy
  // class-level instructor field is empty (staffing lives in the canonical
  // instructor_class_assignments table, not on the class row).
  const override = Number(input.semesterPriceOverride);
  if (Number.isFinite(override) && override > 0) {
    return Math.round(override * 100) / 100;
  }
  if (input.hasInstructor === false) return null;

  const price = Number(input.pricePerChild);
  if (!Number.isFinite(price) || price <= 0) return null;


  switch (input.billingUnit) {
    case "per_semester":
      return price;
    case "per_month": {
      const months = Number(input.semesterMonths);
      if (!Number.isFinite(months) || months <= 0) return null;
      return Math.round(price * months * 100) / 100;
    }
    case "per_session":
    default: {
      const weeks = Number(input.instructionalWeeks);
      if (!Number.isFinite(weeks) || weeks <= 0) return null;
      const bundle = Math.max(1, Number(input.sessionLengthWeeks) || 1);
      const perWeek = price / bundle;
      return Math.round(perWeek * weeks * 100) / 100;
    }
  }
}

/**
 * Render "Fall Semester • $120" style label for a session card, or fall back to
 * just the base label when the total can't be computed.
 */
export function formatSemesterSessionLabel(
  baseLabel: string,
  total: number | null,
): string {
  if (total == null) return baseLabel;
  return `${baseLabel} • ${formatMoney(total)}`;
}

